import React, { useEffect, useState } from "react";
import { api, apiJson } from "@/api/client.js";
import {
  BUTCHER,
  CAMERAS,
  COMMS,
  NO_NEW_POINTS,
  READERS,
  RECOVERY,
  SEPAQ_MAIL_NOTE,
} from "@/content/huntBoard.js";
import { SEPAQ_OFFICIAL } from "@/content/sepaqHunt2026.js";
import { GroupMap } from "@/components/GroupMap.jsx";

const HUNTER_IDS = ["mem_luc", "mem_eric", "mem_marcel", "mem_pierre", "mem_steve", "mem_dan"];
const GAME_URL = "https://www.quebec.ca/tourisme-loisirs-sport/activites-sportives-et-de-plein-air/chasse-sportive/regles-generales/enregistrement-gibier/comment-enregistrer-gibier";
const GAME_ONLINE = "https://www.quebec.ca/enregistrement-gibier";
const SOURCES = [
  ["Québec — chasse à l’orignal", "https://www.quebec.ca/tourisme-loisirs-sport/activites-sportives-et-de-plein-air/chasse-sportive/gibier/orignal"],
  ["Québec — périodes 2026", "https://www.quebec.ca/tourisme-loisirs-sport/activites-sportives-et-de-plein-air/chasse-sportive/periodes-limites/orignal"],
  ["Québec — enregistrement du gibier", GAME_URL],
  ["Québec — dossard", "https://www.quebec.ca/tourisme-loisirs-sport/activites-sportives-et-de-plein-air/chasse-sportive/regles-generales/dossard"],
  ["Sépaq — Réserve faunique de Portneuf", "https://www.sepaq.com/rf/por/"],
];

const DUTIES = [
  ["Steve", [".270 de secours + étui double", "Sawzall", "Appâts / urine d’orignal sur cordes"]],
  ["Luc", ["Lames pour la Sawzall", "Walkie-talkies", "2 bidons d’eau de ville", "Gaz pour les équipements"]],
  ["Éric", ["Premiers soins", "Walkie-talkies", "Bouteilles d’eau", "Gaz pour les équipements"]],
  ["Marcel", ["Laptop", "iPad", "Lecteurs et adaptateurs de cartes mémoire"]],
];

const PLACES = [
  ["Forêt mixte", "Couvert fermé, déplacements lents."],
  ["Coupe en régénération", "Jeunes tiges, nourriture et cache."],
  ["Éclaircie", "On voit plus loin. Le vent porte."],
  ["Aulnaie", "Bord humide, souvent fréquenté."],
  ["Marécage", "Eau et végétation basse."],
  ["Étang", "Point d’eau."],
  ["Bord de lac", "Végétation de rive."],
  ["Transition", "Changement de couvert. S’arrêter pour regarder."],
];

const SIGNS = ["Trace", "Crottin", "Frottage", "Souille", "Passage", "Cri entendu", "Zone nourricière", "Point d’eau", "Caméra", "Observation directe"];

const TIPS = [
  "Vérifier le vent avant de se déplacer.",
  "Ralentir s’il y a du signe frais.",
  "Observer et écouter avant d’avancer.",
  "Regarder les bordures, les ouvertures et les transitions.",
  "Si un secteur est vide, réévaluer plutôt que rester figé.",
  "Consigner une observation intéressante dans NordQuest.",
];

function phoneReady(row) {
  return Boolean(row?.installed && row?.points_imported && row?.offline_ready);
}

const GEAR_TASK = {
  mem_steve: /sawzall|\.270|appât|urine/i,
  mem_luc: /lame|bidon|walkie|gaz/i,
  mem_eric: /premiers soins|bouteille|walkie|gaz|papier de toilette/i,
  mem_marcel: /laptop|ipad|lecteur/i,
};

function taskDone(tasks, id, prefix) {
  return tasks.some((task) => task.assignee_id === id && task.status === "done" && (task.title || "").startsWith(prefix));
}

export function ReadyStrip({ tasks, hunters = [] }) {
  const [nqRows, setNqRows] = useState([]);
  const [kitRows, setKitRows] = useState([]);
  useEffect(() => {
    api("/api/nordquest").then((body) => setNqRows(body.hunters || [])).catch(() => setNqRows([]));
    api("/api/essentials").then((body) => setKitRows(body.hunters || [])).catch(() => setKitRows([]));
  }, []);
  function count(prefix) {
    return HUNTER_IDS.filter((id) => taskDone(tasks, id, prefix)).length;
  }
  const phones = nqRows.filter(phoneReady).length;
  const kits = kitRows.filter((row) => row.done).length;
  const lines = [
    ["Permis", `${count("Acheter / vérifier")} / 6`],
    ["Inscription Sépaq", `${count("Compléter mon inscription")} / 6`],
    ["NordQuest", `${phones} / 6`],
    ["Matériel personnel", `${kits} / 6`],
    ["Matériel commun", "Partiel"],
    ["Véhicules", "Convois connus"],
    ["Nourriture", "Soupers assignés"],
  ];
  const names = Object.fromEntries(hunters.map((hunter) => [hunter.id, hunter.first_name]));
  return (
    <>
      <ul className="og-ready-strip" data-testid="ready-strip">
        {lines.map(([label, value]) => (
          <li key={label}><span>{label}</span><strong>{value}</strong></li>
        ))}
      </ul>
      <ul className="og-score-row" data-testid="hunter-scores">
        {HUNTER_IDS.map((id) => {
          let score = 0;
          if (taskDone(tasks, id, "Acheter / vérifier")) score += 1;
          if (taskDone(tasks, id, "Compléter mon inscription")) score += 1;
          if (phoneReady(nqRows.find((row) => row.member_id === id))) score += 1;
          if (kitRows.find((row) => row.member_id === id)?.done) score += 1;
          const gear = tasks.filter((task) => task.assignee_id === id && GEAR_TASK[id] && GEAR_TASK[id].test(task.title || ""));
          if (gear.length && gear.every((task) => task.status === "done")) score += 1;
          return <li key={id}>{names[id] || id} — <strong>{score}/7</strong></li>;
        })}
      </ul>
    </>
  );
}

export function DutyList() {
  return (
    <div data-testid="duty-list">
      {DUTIES.map(([who, items]) => (
        <section key={who}>
          <h3>{who}</h3>
          <ul>{items.map((item) => <li key={item}>{item}</li>)}</ul>
        </section>
      ))}
      <h3>Groupe</h3>
      <ul>
        {COMMS.map((line) => <li key={line}>{line}</li>)}
        <li>{CAMERAS}</li>
        <li>{READERS}</li>
        {RECOVERY.map((line) => <li key={line}>{line}</li>)}
      </ul>
    </div>
  );
}

export function TerritoryGuide() {
  return (
    <div data-testid="territory-guide">
      <h3>Approcher avec le vent</h3>
      <p>Vérifier le vent avant de se déplacer. Le vent porte dans les éclaircies.</p>
      <h3>Le matin</h3>
      <p>Observer et écouter avant d’avancer. Bordures, ouvertures, transitions.</p>
      <h3>En fin de journée</h3>
      <p>Ralentir. Les bordures et les points d’eau se regardent avant de rentrer.</p>
      <h3>Signes frais</h3>
      <p>Ralentir s’il y a du signe frais. Consigner le point dans NordQuest.</p>
      <h3>Déplacement calme</h3>
      <p>Couvert fermé : déplacements lents. S’arrêter pour regarder.</p>
      <h3>Quoi éviter</h3>
      <p>Ne pas rester figé dans un secteur vide. Identifier clairement l’animal avant toute décision.</p>
      <h3>Où regarder</h3>
      <ul className="og-guide-list">
        {PLACES.map(([name, note]) => <li key={name}><strong>{name}</strong><span>{note}</span></li>)}
      </ul>
      <h3>Signes</h3>
      <ul className="og-sign-row">
        {SIGNS.map((sign) => <li key={sign}>{sign}</li>)}
      </ul>
      <h3>Conseils terrain</h3>
      <p data-testid="advice-label">Conseils pratiques — pas des règles Sépaq</p>
      <ul>{TIPS.map((tip) => <li key={tip}>{tip}</li>)}</ul>
      <p>Identifier clairement l’animal avant toute décision. Le permis et les modalités 2026 restent à vérifier par le chasseur.</p>
    </div>
  );
}

export function HarvestPanel() {
  return (
    <div data-testid="harvest-panel">
      <ol className="og-harvest">
        <li>Marquer la position dans NordQuest.</li>
        <li>Aviser la gang.</li>
        <li>Vérifier le permis et les coupons requis. Ne rien présumer.</li>
        <li>Récupération : {RECOVERY.join(" · ")}</li>
        <li>Conservation : chambre froide à l’accueil. Potence près de l’hébergement.</li>
        <li>{BUTCHER.who} contacte {BUTCHER.name}. {BUTCHER.note}</li>
        <li>Enregistrer le gibier dans les 48 h après la sortie du lieu de chasse, avant le boucher.</li>
        <li>La page Sépaq de Portneuf indique qu’aucun permis de chasse à la femelle n’est attribué pour 2026. Chaque chasseur vérifie son permis.</li>
      </ol>
      <p>Station : {SEPAQ_OFFICIAL.game}. Si Internet manque, la station reste la solution.</p>
      <a className="og-btn og-btn--primary" href={GAME_ONLINE} target="_blank" rel="noreferrer" data-testid="game-register">Enregistrer le gibier</a>
      <a className="og-btn" href={GAME_URL} target="_blank" rel="noreferrer">Comment enregistrer</a>
    </div>
  );
}

export function ReturnPanel({ onOpen, day = "" }) {
  const actions = [
    ["nordquest", "Nouveaux points NordQuest ?"],
    ["observation", "Observations importantes ?"],
    ["gear", "Caméras vérifiées ?"],
    ["expenses", "Dépenses à ajouter ?"],
    ["plan", "Note pour demain ?"],
  ];
  function keep(event) {
    const form = event.currentTarget.form || event.currentTarget.closest("form");
    if (!form || !day) return;
    const note = form.tomorrow?.value || "";
    const checks = {
      points: Boolean(form.points?.checked),
      observations: Boolean(form.observations?.checked),
      cameras: Boolean(form.cameras?.checked),
      expenses: Boolean(form.expenses?.checked),
    };
    window.localStorage.setItem(`orignal2026.return.${day}`, JSON.stringify({ ...checks, tomorrow: note }));
  }
  return (
    <div data-testid="return-panel">
      <p>Retour au chalet</p>
      <p>{CAMERAS} {READERS}</p>
      <form className="og-task-compose" data-testid="return-questions" onChange={keep}>
        <label><input type="checkbox" name="points" /> Nouveaux points NordQuest</label>
        <label><input type="checkbox" name="observations" /> Observations importantes</label>
        <label><input type="checkbox" name="cameras" /> Caméras vérifiées</label>
        <label><input type="checkbox" name="expenses" /> Dépenses à ajouter</label>
        <label>Note pour demain<textarea className="og-input" name="tomorrow" rows={2} onBlur={keep} /></label>
      </form>
      <GroupMap mode="return" />
      <div className="og-fit__actions">
        {actions.map(([key, label]) => (
          <button key={key} type="button" className="og-tile" onClick={() => onOpen(key)}>{label}</button>
        ))}
      </div>
    </div>
  );
}

const SIGN_TYPES = ["Traces", "Crottin", "Frottage", "Souille", "Passage", "Zone nourricière", "Eau", "Coupe", "Bordure", "Zone humide", "Autre"];

export function ObservationForm({ day = "" }) {
  const [sent, setSent] = useState("");
  const [photo, setPhoto] = useState(null);
  async function submit(event) {
    event.preventDefault();
    const form = event.target;
    const title = `${form.kind.value} · ${form.place.value || "lieu à préciser"}`;
    let photoLine = "";
    if (photo) {
      try {
        const body = new FormData();
        body.append("file", photo);
        body.append("category", "observations");
        const media = await api("/api/media", { method: "POST", body });
        if (media?.url) photoLine = `Photo : ${media.url}`;
      } catch {
        photoLine = "Photo non envoyée. La note est quand même enregistrée.";
      }
    }
    const note = [
      form.when.value && `Heure : ${form.when.value}`,
      form.direction.value && `Direction : ${form.direction.value}`,
      form.sky.value && `Météo observée : ${form.sky.value}`,
      form.zones.value && `Zones : ${form.zones.value}`,
      form.points.value && `Nouveaux points : ${form.points.value}`,
      form.summary.value && `Résumé : ${form.summary.value}`,
      form.note.value,
      photoLine,
    ].filter(Boolean).join("\n");
    const stamp = form.when.value && day ? `${day}T${form.when.value}` : new Date().toISOString().slice(0, 16);
    try {
      await apiJson("/api/observations", {
        title,
        body: note,
        obs_type: "signe",
        category: "orignal",
        observed_at: stamp,
      });
      setSent("Noté pour la gang.");
      setPhoto(null);
      form.reset();
    } catch {
      setSent("Pas enregistré. Réessayer au chalet.");
    }
  }
  return (
    <form className="og-task-compose" data-testid="observation-form" onSubmit={submit}>
      <p>Un signe observé. Aucun conseil de tir.</p>
      <label>Heure<input className="og-input" name="when" type="time" /></label>
      <label>Lieu / point NordQuest<input className="og-input" name="place" placeholder="Point ou lieu" /></label>
      <label>Type de signe
        <select className="og-input" name="kind" defaultValue="Traces">
          {SIGN_TYPES.map((kind) => <option key={kind}>{kind}</option>)}
        </select>
      </label>
      <label>Direction<input className="og-input" name="direction" /></label>
      <label>Météo observée<input className="og-input" name="sky" /></label>
      <label>Zones visitées<input className="og-input" name="zones" /></label>
      <label>Nouveaux points<input className="og-input" name="points" /></label>
      <label>Résumé de la journée<textarea className="og-input" name="summary" rows={2} /></label>
      <label>Note<textarea className="og-input" name="note" rows={3} /></label>
      <label>Photo facultative<input className="og-input" type="file" accept="image/*" onChange={(event) => setPhoto(event.target.files?.[0] || null)} /></label>
      <button className="og-btn og-btn--primary" type="submit">Enregistrer l’observation</button>
      {sent ? <p>{sent}</p> : null}
    </form>
  );
}

export function PointVersionNote() {
  return <p data-testid="point-version">Version 1 — 9 points d’Éric, 29 août. {NO_NEW_POINTS}</p>;
}

const SHORT_KIT = [
  ["vest", "Dossard fluorescent"],
  ["idcard", "Pièce d’identité"],
  ["bedding", "Literie"],
  ["rain", "Vêtements de pluie"],
  ["boots", "Bottes"],
  ["lamp", "Lampe"],
  ["phone", "Téléphone"],
  ["chargers", "Chargeur"],
];

export function PersonPack({ memberId, onNordQuest }) {
  const [phone, setPhone] = useState(null);
  const [kit, setKit] = useState(null);
  useEffect(() => {
    api("/api/nordquest").then((body) => setPhone((body.hunters || []).find((row) => row.member_id === memberId) || null)).catch(() => setPhone(null));
    api("/api/essentials").then((body) => setKit((body.hunters || []).find((row) => row.member_id === memberId) || null)).catch(() => setKit(null));
  }, [memberId]);
  async function toggle(key) {
    if (!kit?.can_edit) return;
    const next = !kit.checks?.[key];
    setKit((current) => ({ ...current, checks: { ...(current?.checks || {}), [key]: next } }));
    try {
      await apiJson(`/api/essentials/${memberId}`, { checks: { [key]: next } }, "PUT");
    } catch {
      /* la case reste pour cette séance */
    }
  }
  return (
    <section data-testid="person-pack">
      <h3>CARTES</h3>
      <ul data-testid="person-nordquest">
        <li>NordQuest — {phone?.installed ? "Installé ✓" : "À faire"}</li>
        <li>Carte officielle Sépaq — Portneuf — {phone?.sepaq_downloaded ? "Téléchargée ✓" : "À faire"}</li>
        <li>Carte du groupe — Version V1</li>
        <li>9 points visibles — {phone?.points_imported ? "✓" : "À faire"}</li>
        <li>Hors ligne — {phone?.offline_ready ? "Test réussi ✓" : "À faire"}</li>
      </ul>
      <button type="button" className="og-btn og-btn--primary" data-testid="person-nordquest-go" onClick={onNordQuest}>Préparer mon téléphone</button>
      <h3>Essentiels personnels</h3>
      <ul>
        {SHORT_KIT.map(([key, label]) => (
          <li key={key}>
            <label>
              <input type="checkbox" checked={Boolean(kit?.checks?.[key])} disabled={!kit?.can_edit} onChange={() => toggle(key)} />
              {label}
            </label>
          </li>
        ))}
      </ul>
    </section>
  );
}

export function PermitReminder() {
  return <p>{SEPAQ_MAIL_NOTE} Le permis se confirme par le chasseur, jamais par l’application.</p>;
}

export function OfficialSources() {
  return (
    <div data-testid="official-sources">
      <ul>
        {SOURCES.map(([label, href]) => (
          <li key={href}><a href={href} target="_blank" rel="noreferrer">{label}</a></li>
        ))}
        <li>Fiche technique du séjour — le document déjà dans l’application.</li>
      </ul>
      <p>Les règles peuvent changer. Chaque chasseur vérifie les modalités au moment de chasser.</p>
    </div>
  );
}
